// client/src/components/event/EventLifecycleBadge.jsx
//
// Event lifecycle status pill + inline transition menu (On-Screen 2.0).
// Transitions are validated server-side by services/eventLifecycle.js; this
// component optimistically shows the new status and rolls back on error.

import React, { useState } from 'react';
import { changeEventStatus, EVENT_LIFECYCLE, EVENT_LIFECYCLE_LABELS } from '../../services/eventsApi';

const PHASE = {
  draft: 'plan', planning: 'plan',
  promoting: 'promote', registration_open: 'promote',
  registration_closed: 'execute', in_progress: 'execute',
  completed: 'followup', archived: 'followup',
};

// Explicit palette per phase — text always on an opaque surface.
const SURFACE = {
  plan: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  promote: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  execute: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  followup: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
};

export default function EventLifecycleBadge({
  tenantId, eventId, status, canEdit = false, onChange, compact = false,
}) {
  const [current, setCurrent] = useState(status || 'draft');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => { setCurrent(status || 'draft'); }, [status]);

  const surface = SURFACE[PHASE[current]] || SURFACE.plan;
  const label = EVENT_LIFECYCLE_LABELS[current] || current;

  const pick = async (next) => {
    if (next === current || busy) return;
    setBusy(true);
    setError('');
    const prev = current;
    setCurrent(next);
    setOpen(false);
    try {
      const res = await changeEventStatus(tenantId, eventId, next);
      if (onChange) onChange(res.status || next);
    } catch (err) {
      setCurrent(prev);
      setError(err?.response?.data?.message || err.message || 'Could not change status');
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) {
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${surface}`}>
        {label}
      </span>
    );
  }

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${surface} disabled:opacity-50`}
      >
        {busy ? 'Saving…' : label}
        <span aria-hidden className="opacity-60">▾</span>
      </button>

      {open && (
        <div
          className="absolute left-0 z-30 mt-1 w-48 rounded-lg border border-black/10 bg-white p-1 text-sm shadow-lg dark:border-white/15 dark:bg-neutral-900"
          onMouseLeave={() => setOpen(false)}
        >
          {EVENT_LIFECYCLE.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => pick(s)}
              className={`block w-full rounded px-2 py-1.5 text-left hover:bg-black/5 dark:hover:bg-white/10 ${
                s === current ? 'font-semibold' : ''
              }`}
            >
              {EVENT_LIFECYCLE_LABELS[s]}
            </button>
          ))}
        </div>
      )}

      {error && !compact && (
        <span className="ml-2 text-xs text-red-600">{error}</span>
      )}
    </span>
  );
}
